import React from 'react';
import { Building2, User, Briefcase, CheckSquare, ExternalLink } from 'lucide-react';

/**
 * EntityLinks — Compact bar of clickable related-entity quick-links.
 *
 * Props:
 *  - company: { _id, name } or null
 *  - contact: { _id, name/email } or null
 *  - opportunity: { _id, title } or null
 *  - taskCount: number or null
 *  - onNavigate(entityType, entityId): callback
 */
export default function EntityLinks({ company, contact, opportunity, taskCount, onNavigate }) {
    const links = [];

    if (company && company.name) {
        links.push({
            key: 'company',
            icon: <Building2 className="w-3.5 h-3.5" />,
            label: company.name,
            onClick: () => onNavigate('company', company._id)
        });
    }

    if (contact) {
        let label = 'Contact';
        if (typeof contact === 'object') {
            const pm = contact.personMasterId;
            const pmName = pm?.firstName ? `${pm.firstName} ${pm.lastName || ''}`.trim() : (pm?.fullName || pm?.displayName);
            label = pmName || contact.displayName || pm?.canonicalEmail || contact.email || contact.displayEmail || 'Contact';
        }
        
        links.push({
            key: 'contact',
            icon: <User className="w-3.5 h-3.5" />,
            label: typeof label === 'string' ? label : 'Contact',
            onClick: () => onNavigate('contact', contact._id || contact.id)
        });
    }

    if (opportunity && opportunity.title) {
        links.push({
            key: 'opportunity',
            icon: <Briefcase className="w-3.5 h-3.5" />,
            label: opportunity.title,
            onClick: () => onNavigate('opportunity', opportunity._id)
        });
    }

    if (taskCount !== null && taskCount !== undefined) {
        links.push({
            key: 'tasks',
            icon: <CheckSquare className="w-3.5 h-3.5" />,
            label: `${taskCount} Task${taskCount !== 1 ? 's' : ''}`,
            onClick: () => onNavigate('tasks', null)
        });
    }

    if (links.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Related</span>
            {links.map(link => (
                <button
                    key={link.key}
                    onClick={link.onClick}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg border border-gray-200 hover:border-indigo-200 transition-colors group"
                >
                    <span className="text-gray-400 group-hover:text-indigo-500">{link.icon}</span>
                    <span className="truncate max-w-[120px]">{link.label}</span>
                    <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-indigo-400" />
                </button>
            ))}
        </div>
    );
}
